import React, { useContext, useState } from 'react';
import { Button, Switch } from '@warp-ds/react';
import { SchibstedPrivacySettingsContext } from '../schibsted-privacy-settings-context.js';
import { Scope } from '../../scope.js';
import { useLingui } from '@lingui/react';
import TranslationContext from '../translation-context.js';
import ChevronUp from '@warp-ds/icons/react/chevron-up-16';
import ChevronDown from '@warp-ds/icons/react/chevron-down-16';

function isConsentSetting(
    setting: ConsentSetting | ObjectionSetting,
): setting is ConsentSetting {
    return (setting as ConsentSetting).currentState.hasConsent !== undefined;
}

function ReadMore(props: {
    category: ObjectionCategory | ConsentCategory;
}): React.ReactElement {
    const { i18n } = useLingui();
    const { texts } = useContext(TranslationContext);
    const [active, setActive] = useState(false);
    const toggle = () => setActive(!active);

    const readMoreText =
        props.category.settings
            .flatMap((setting) => setting.content.modalInformation)
            .filter((value) => value !== undefined)[0] || undefined;

    return (
        (readMoreText && (
            <>
                <Button
                    onClick={toggle}
                    aria-expanded={active}
                    className="flex items-center"
                    secondary
                    small
                    quiet
                >
                    <span className="mr-4 text-xs">
                        {active
                            ? i18n.t({
                                  id: 'privacy-settings.read-less',
                                  comment: 'Read less link',
                                  values: texts,
                              })
                            : i18n.t({
                                  id: 'privacy-settings.read-more',
                                  comment: 'Read more link',
                                  values: texts,
                              })}
                    </span>
                    {active ? <ChevronUp /> : <ChevronDown />}
                </Button>
                <div className={active ? 'block py-8' : 'hidden'}>
                    {readMoreText}
                </div>
            </>
        )) || <></>
    );
}

export default function Category(props: {
    category: ObjectionCategory | ConsentCategory;
    scope: Scope;
    isLast: boolean;
}): React.ReactElement {
    return (
        <>
            <div className="mt-32">
                <h3 className="mb-16">{props.category.categoryTitle}</h3>
                <p>
                    {
                        // Show the first category description that is not undefined
                        props.category.settings
                            .flatMap((setting) => setting.content.categoryDesc)
                            .filter((value) => value !== undefined)[0] || ''
                    }
                </p>
                <ReadMore category={props.category} />
                <div>
                    {props.category.settings.map((setting, index) =>
                        isConsentSetting(setting) ? (
                            <ConsentToggle
                                key={setting.key.id}
                                setting={setting}
                                isLast={
                                    index === props.category.settings.length - 1
                                }
                                scope={props.scope}
                            />
                        ) : (
                            <ObjectionToggle
                                key={setting.key.id}
                                setting={setting}
                                isLast={
                                    index === props.category.settings.length - 1
                                }
                                scope={props.scope}
                            />
                        ),
                    )}
                </div>
            </div>
            {props.isLast ? '' : <hr className="my-16" />}
        </>
    );
}

export function ObjectionToggle(props: {
    setting: ObjectionSetting;
    isLast: boolean;
    scope: Scope;
}): React.ReactElement {
    const ctx = useContext(SchibstedPrivacySettingsContext);
    const [updating, setUpdating] = useState(false);

    const changeSetting = () => {
        if (!updating) {
            setUpdating(true);
            if (props.setting.currentState.hasObjection) {
                if (props.setting.currentState.objectionKey) {
                    ctx.withdrawObjection(props.setting, props.scope).finally(
                        () => setUpdating(false),
                    );
                }
            } else {
                ctx.addObjection(props.setting, props.scope).finally(() =>
                    setUpdating(false),
                );
            }
        }
    };

    return (
        <div className="py-16">
            <div className="flex">
                <div className="grow pr-16">
                    <h3 className="text-m">{props.setting.content.title}</h3>
                </div>
                <Switch
                    value={!props.setting.currentState.hasObjection}
                    onClick={changeSetting}
                    aria-label={props.setting.content.title}
                />
            </div>
        </div>
    );
}

export function ConsentToggle(props: {
    setting: ConsentSetting;
    isLast: boolean;
    scope: Scope;
}): React.ReactElement {
    const ctx = useContext(SchibstedPrivacySettingsContext);
    const [updating, setUpdating] = useState(false);

    const changeSetting = () => {
        if (!updating) {
            setUpdating(true);
            if (props.setting.currentState.hasConsent) {
                if (props.setting.currentState.consentKey) {
                    ctx.withdrawConsent(props.setting, props.scope).finally(
                        () => setUpdating(false),
                    );
                }
            } else {
                ctx.addConsent(props.setting, props.scope).finally(() =>
                    setUpdating(false),
                );
            }
        }
    };

    return (
        <div className="py-16">
            <div className="flex">
                <div className="grow pr-16">
                    <h3 className="text-m">{props.setting.content.title}</h3>
                </div>
                <Switch
                    value={props.setting.currentState.hasConsent}
                    onClick={changeSetting}
                    aria-label={props.setting.content.title}
                />
            </div>
        </div>
    );
}
